import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import authApi from '../../api/authApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { format, getUnixTime, startOfDay } from 'date-fns';
import formattedMoney from '../../utils/formatMoney';
import { Link } from 'react-router-dom';
import TopCashBackPodium from './top-cashback-podium';
import TopCashBackPrizes from './top-cashback-prizes';
import Top4 from './Top4';

const TODAY = new Date();
const YEAR = TODAY.getFullYear();
const MONTH = TODAY.getMonth();

const makeTimeRange = (monthOffset) => ({
    startTime: getUnixTime(startOfDay(new Date(YEAR, MONTH + monthOffset, 1))),
    endTime: getUnixTime(startOfDay(new Date(YEAR, MONTH + monthOffset + 1, 1))),
});

function CommissionModal({ user, onClose }) {
    if (!user) return null;
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-2xl p-5 w-full max-w-sm shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-base font-bold">Chi tiết hoàn tiền</h3>
                    <button onClick={onClose} className="p-1.5 hover:bg-surface-muted rounded-full transition-colors">
                        <FontAwesomeIcon icon={faTimes} className="text-content-3" />
                    </button>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center p-3 bg-surface-sunken rounded-xl">
                        <span className="text-sm font-semibold">Tổng hoàn tiền</span>
                        <span className="font-bold text-primary-600">{formattedMoney.format(user.totalUserCommission || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                        <span className="text-sm text-green-700">Đã duyệt</span>
                        <span className="font-semibold text-green-600">{formattedMoney.format(user.confirmedCommission || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-xl">
                        <span className="text-sm text-orange-700">Chờ duyệt</span>
                        <span className="font-semibold text-orange-500">{formattedMoney.format(user.pendingCommission || 0)}</span>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="mt-4 w-full bg-primary-600 text-white py-2.5 rounded-xl font-semibold hover:bg-primary-700 transition-colors"
                >
                    Đóng
                </button>
            </div>
        </div>
    );
}

function TopCashBackV2() {
    const [users, setUsers] = useState([]);
    const [isThisMonth, setIsThisMonth] = useState(true);
    const [time, setTime] = useState(makeTimeRange(0));
    const [selectedUser, setSelectedUser] = useState(null);

    const displayMonth = isThisMonth ? MONTH + 1 : MONTH === 0 ? 12 : MONTH;
    const displayYear = isThisMonth ? YEAR : MONTH === 0 ? YEAR - 1 : YEAR;

    const handleTimeChange = (thisMonth) => {
        if (thisMonth === isThisMonth) return;
        setIsThisMonth(thisMonth);
        setTime(makeTimeRange(thisMonth ? 0 : -1));
    };

    useEffect(() => {
        const fetchUsers = async () => {
            const ONLY_NUM_REGEX = /^\d+$/;
            try {
                const { data } = await authApi.topOfMonth(time.startTime, time.endTime);
                const valid = data.userList.filter(
                    (el) => el.userId && el.userId !== '' && ONLY_NUM_REGEX.test(el.userId)
                );
                const list = valid.slice(0, 20);
                const fill = new Array(Math.max(0, 20 - list.length)).fill({
                    userId: '-',
                    totalUserCommission: 0,
                    confirmedCommission: 0,
                    pendingCommission: 0,
                });
                setUsers([...list, ...fill]);
            } catch (e) {
                console.log(e);
            }
        };
        fetchUsers();
    }, [time]);

    return (
        <div className="min-h-screen bg-surface-sunken">
            <Header hasBackButton redirectPath="/" />

            {/* Hero banner */}
            <div className="pt-16 bg-gradient-to-b from-red-600 via-orange-500 to-orange-400">
                <div className="text-center pt-5 pb-3 text-white md:w-[768px] lg:w-[1024px] mx-auto">
                    <p className="text-xs font-medium uppercase tracking-widest opacity-80">Sự kiện hàng tháng</p>
                    <h1 className="font-lexend font-extrabold text-2xl mt-1 drop-shadow">ĐUA TOP HOÀN TIỀN</h1>
                    <p className="text-sm font-medium mt-1 opacity-90">Tháng {displayMonth}/{displayYear}</p>

                    {/* Time filter tabs */}
                    <div className="flex justify-center gap-2 mt-3">
                        <button
                            onClick={() => handleTimeChange(false)}
                            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                                !isThisMonth ? 'bg-surface text-orange-600' : 'bg-surface/20 text-white'
                            }`}
                        >
                            Tháng trước
                        </button>
                        <button
                            onClick={() => handleTimeChange(true)}
                            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                                isThisMonth ? 'bg-surface text-orange-600' : 'bg-surface/20 text-white'
                            }`}
                        >
                            Tháng này
                        </button>
                    </div>
                </div>

                {/* Podium top 3 */}
                <div className="md:w-[768px] lg:w-[1024px] mx-auto">
                    {users.length > 0 && (
                        <TopCashBackPodium users={users} onUserClick={setSelectedUser} />
                    )}
                </div>
            </div>

            {/* White content card */}
            <div className="bg-surface rounded-t-3xl -mt-3 shadow-lg md:w-[768px] lg:w-[1024px] mx-auto">
                <TopCashBackPrizes />
                <Top4 users={users.slice(3)} onCommissionClick={setSelectedUser} />
                <div className="text-center text-xs text-content-3 pb-10 px-4 space-y-1 pt-2">
                    <p>Cập nhật lần cuối: {format(new Date(), 'HH:mm dd/MM/yyyy')}</p>
                    <p>
                        Xem Quy định tham gia{' '}
                        <Link to="/post/21" className="font-semibold text-primary-600 underline">
                            TẠI ĐÂY
                        </Link>
                    </p>
                </div>
            </div>

            <CommissionModal user={selectedUser} onClose={() => setSelectedUser(null)} />
        </div>
    );
}

export default TopCashBackV2;
